import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import "../../styles/Blog.css";
import NotFound from "../NotFound";
import strings from "../../blog/strings";
import useBlogHead from "../../blog/seo";
import { blogPostingLd, breadcrumbLd, faqPageLd } from "../../blog/schema";
import {
  getPost,
  getPostsBySlugs,
  getAuthor,
  getTags,
  langConfig,
  absolute,
} from "../../blog/data";
import {
  Breadcrumbs,
  PostMeta,
  TagList,
  RelatedPosts,
} from "./parts";

function BlogPost({ lang }) {
  const { slug } = useParams();
  const post = getPost(lang, slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) return <NotFound />;

  const t = strings(lang);
  const cfg = langConfig(lang);
  const author = getAuthor(lang, post.author);
  const related = getPostsBySlugs(lang, post.related);
  const crumbs = [
    { name: t.home, path: "/" },
    { name: cfg.title, path: cfg.indexPath },
    { name: post.title },
  ];

  return (
    <div className="blog">
      <BlogPostHead
        lang={lang}
        post={post}
        author={author}
        cfg={cfg}
        crumbs={crumbs}
      />

      <div className="blog-shell blog-shell-article">
        <Breadcrumbs lang={lang} items={crumbs} />

        <article className="blog-article">
          <header className="blog-article-header">
            <h1>{post.title}</h1>
            <p className="blog-lead">{post.description}</p>
            <PostMeta lang={lang} post={post} />
            <p className="blog-byline">
              {t.authorPrefix}: <Link to={author.path}>{author.name}</Link>
            </p>
          </header>

          {post.toc.length > 0 && (
            <nav className="blog-toc" aria-labelledby="blog-toc-heading">
              <h2 id="blog-toc-heading">{t.tableOfContents}</h2>
              <ol>
                {post.toc.map((h) => (
                  <li key={h.id}>
                    <a href={`#${h.id}`}>{h.text}</a>
                  </li>
                ))}
              </ol>
            </nav>
          )}

          <div
            className="blog-content"
            /* HTML pochodzi wyłącznie z plików Markdown w repo (build-time) */
            dangerouslySetInnerHTML={{ __html: post.html }}
          />

          <footer className="blog-article-footer">
            <TagList lang={lang} tags={post.tags} allTags={getTags(lang)} />
            <p className="blog-back">
              <Link to={cfg.indexPath}>{t.backToBlog}</Link>
            </p>
          </footer>
        </article>

        <RelatedPosts lang={lang} posts={related} />
      </div>
    </div>
  );
}

function BlogPostHead({ lang, post, author, cfg, crumbs }) {
  useBlogHead({
    title: post.seoTitle,
    description: post.description,
    canonical: post.url,
    lang,
    locale: cfg.locale,
    ogType: "article",
    image: post.ogImage,
    alternates: post.alternates || undefined,
    rss: cfg.rssUrl,
    article: {
      publishedTime: post.datePublished,
      modifiedTime: post.dateModified,
      author: author.url,
      tags: post.tags,
    },
    jsonLd: [
      blogPostingLd(post, author),
      breadcrumbLd(
        crumbs.map((c) => ({
          name: c.name,
          url: c.path ? absolute(c.path) : post.url,
        }))
      ),
      ...(post.faq && post.faq.length ? [faqPageLd(post.faq)] : []),
    ],
  });
  return null;
}

export default BlogPost;
